// src/redux/notification/notificationSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getNotificationsAPI,
  getUnreadNotificationCountAPI,
  markNotificationAsReadAPI,
  markAllNotificationsAsReadAPI,
  deleteNotificationAPI,
} from "./Notificationapi";

// ✅ Get Notifications
export const getNotifications = createAsyncThunk(
  "notification/getNotifications",
  async (
    { page = 1, limit = 20, isRead, startDate, endDate } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await getNotificationsAPI({
        page,
        limit,
        isRead,
        startDate,
        endDate,
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notifications"
      );
    }
  }
);

// ✅ Get Unread Notification Count
export const getUnreadNotificationCount = createAsyncThunk(
  "notification/getUnreadNotificationCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUnreadNotificationCountAPI();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch unread notification count"
      );
    }
  }
);

// ✅ Mark Notification as Read
export const markNotificationAsRead = createAsyncThunk(
  "notification/markNotificationAsRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await markNotificationAsReadAPI(notificationId);
      return { ...response, notificationId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to mark notification as read"
      );
    }
  }
);

// ✅ Mark All Notifications as Read
export const markAllNotificationsAsRead = createAsyncThunk(
  "notification/markAllNotificationsAsRead",
  async (_, { rejectWithValue }) => {
    try {
      const response = await markAllNotificationsAsReadAPI();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to mark all notifications as read"
      );
    }
  }
);

// ✅ Delete Notification
export const deleteNotification = createAsyncThunk(
  "notification/deleteNotification",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await deleteNotificationAPI(notificationId);
      return { ...response, notificationId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete notification"
      );
    }
  }
);

// ---------- Initial State ----------
const initialState = {
  getNotifications: {
    data: [],
    total: 0,
    loading: false,
    error: null,
  },
  getUnreadNotificationCount: {
    data: null,
    loading: false,
    error: null,
  },
  markNotificationAsRead: {
    loading: false,
    error: null,
  },
  markAllNotificationsAsRead: {
    loading: false,
    error: null,
  },
  deleteNotification: {
    loading: false,
    error: null,
  },
};

// ---------- Slice ----------
const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    clearNotifications: (state) => {
      state.getNotifications.data = [];
      state.getNotifications.total = 0;
      state.getNotifications.error = null;
    },
    clearUnreadNotificationCount: (state) => {
      state.getUnreadNotificationCount.data = null;
      state.getUnreadNotificationCount.error = null;
    },
    clearMarkNotificationAsRead: (state) => {
      state.markNotificationAsRead.error = null;
    },
    clearMarkAllNotificationsAsRead: (state) => {
      state.markAllNotificationsAsRead.error = null;
    },
    clearDeleteNotification: (state) => {
      state.deleteNotification.error = null;
    },
  },
  extraReducers: (builder) => {
    // ---------- Get Notifications ----------
    builder
      .addCase(getNotifications.pending, (state) => {
        state.getNotifications.loading = true;
        state.getNotifications.error = null;
      })
      .addCase(getNotifications.fulfilled, (state, action) => {
        state.getNotifications.loading = false;
        // Extract notifications from action.payload.data.data (API structure)
        if (Array.isArray(action.payload?.data?.data)) {
          state.getNotifications.data = action.payload.data.data;
        } else if (Array.isArray(action.payload?.data)) {
          state.getNotifications.data = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          state.getNotifications.data = action.payload;
        } else {
          state.getNotifications.data = [];
        }
        state.getNotifications.total =
          action.payload?.data?.pagination?.total || action.payload?.pagination?.total || 0;
      })
      .addCase(getNotifications.rejected, (state, action) => {
        state.getNotifications.loading = false;
        state.getNotifications.data = [];
        state.getNotifications.error = action.payload;
      });

    // ---------- Get Unread Notification Count ----------
    builder
      .addCase(getUnreadNotificationCount.pending, (state) => {
        state.getUnreadNotificationCount.loading = true;
        state.getUnreadNotificationCount.error = null;
      })
      .addCase(getUnreadNotificationCount.fulfilled, (state, action) => {
        state.getUnreadNotificationCount.loading = false;
        state.getUnreadNotificationCount.data =
          action.payload?.data || action.payload;
      })
      .addCase(getUnreadNotificationCount.rejected, (state, action) => {
        state.getUnreadNotificationCount.loading = false;
        state.getUnreadNotificationCount.error = action.payload;
      });

    // ---------- Mark Notification as Read ----------
    builder
      .addCase(markNotificationAsRead.pending, (state) => {
        state.markNotificationAsRead.loading = true;
        state.markNotificationAsRead.error = null;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        state.markNotificationAsRead.loading = false;
        // Optimistically update the notification in the list
        const { notificationId } = action.payload;
        const index = state.getNotifications.data.findIndex(
          (n) => n.id === notificationId
        );
        if (index !== -1) {
          state.getNotifications.data[index].isRead = true;
        }
        // Decrement unread count if available
        if (state.getUnreadNotificationCount.data?.count > 0) {
          state.getUnreadNotificationCount.data.count -= 1;
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.markNotificationAsRead.loading = false;
        state.markNotificationAsRead.error = action.payload;
      });

    // ---------- Mark All Notifications as Read ----------
    builder
      .addCase(markAllNotificationsAsRead.pending, (state) => {
        state.markAllNotificationsAsRead.loading = true;
        state.markAllNotificationsAsRead.error = null;
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.markAllNotificationsAsRead.loading = false;
        // Optimistically mark all notifications as read in the list
        state.getNotifications.data = state.getNotifications.data.map((n) => ({
          ...n,
          isRead: true,
        }));
        // Reset unread count to 0
        if (state.getUnreadNotificationCount.data) {
          state.getUnreadNotificationCount.data.count = 0;
        }
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.markAllNotificationsAsRead.loading = false;
        state.markAllNotificationsAsRead.error = action.payload;
      });

    // ---------- Delete Notification ----------
    builder
      .addCase(deleteNotification.pending, (state) => {
        state.deleteNotification.loading = true;
        state.deleteNotification.error = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.deleteNotification.loading = false;
        // Optimistically remove the notification from the list
        const { notificationId } = action.payload;
        state.getNotifications.data = state.getNotifications.data.filter(
          (n) => n.id !== notificationId
        );
        if (state.getNotifications.total > 0) {
          state.getNotifications.total -= 1;
        }
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.deleteNotification.loading = false;
        state.deleteNotification.error = action.payload;
      });
  },
});

export const {
  clearNotifications,
  clearUnreadNotificationCount,
  clearMarkNotificationAsRead,
  clearMarkAllNotificationsAsRead,
  clearDeleteNotification,
} = notificationSlice.actions;

export default notificationSlice.reducer;